import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import Swal from "sweetalert2";
import { Spinner } from "../commons/Spinner";

const AdminFeriados = () => {
    const [feriados, setFeriados] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({ fechaInicio: "", fechaFin: "", motivo: "" });

    const fetchFeriados = async () => {
        try {
            const snapshot = await getDocs(collection(db, "feriados"));
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Ordenamos por fecha de inicio (o por la fecha única vieja si existe)
            data.sort((a, b) => {
                const dateA = new Date(a.fechaInicio || a.fecha);
                const dateB = new Date(b.fechaInicio || b.fecha);
                return dateA - dateB;
            });
            setFeriados(data);
        } catch (error) {
            console.error("Error al obtener feriados", error);
        }
    };

    useEffect(() => {
        fetchFeriados();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.fechaInicio || !formData.motivo) return;

        // Si no pone fecha fin, asumimos que es de un solo día (mismo día que inicio)
        const fechaFinFinal = formData.fechaFin || formData.fechaInicio;

        // Validar que la fecha de fin no sea menor a la de inicio
        if (fechaFinFinal < formData.fechaInicio) {
            Swal.fire({ icon: "error", title: "Error en las fechas", text: "La fecha de fin no puede ser anterior a la fecha de inicio." });
            return;
        }

        setIsLoading(true);
        try {
            await addDoc(collection(db, "feriados"), {
                fechaInicio: formData.fechaInicio,
                fechaFin: fechaFinFinal,
                motivo: formData.motivo
            });
            Swal.fire({ icon: "success", title: "Período agregado", showConfirmButton: false, timer: 1500 });
            setFormData({ fechaInicio: "", fechaFin: "", motivo: "" });
            fetchFeriados();
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: "No se pudo agregar." });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "¿Eliminar registro?",
            text: "La oficina volverá a figurar como abierta en esas fechas.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#7A1B29",
            confirmButtonText: "Sí, eliminar"
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, "feriados", id));
                Swal.fire({ icon: "success", title: "Eliminado", showConfirmButton: false, timer: 1500 });
                fetchFeriados();
            } catch (error) {
                console.error("Error eliminando", error);
            }
        }
    };

    const formatDisplayDate = (f) => {
        // Para compatibilidad con datos viejos de un solo día
        if (f.fecha) return f.fecha.split("-").reverse().join("/");

        // Si es un solo día en el formato nuevo
        if (f.fechaInicio === f.fechaFin) {
            return f.fechaInicio.split("-").reverse().join("/");
        }

        // Si es un rango
        return `Del ${f.fechaInicio.split("-").reverse().join("/")} al ${f.fechaFin.split("-").reverse().join("/")}`;
    };

    return (
        <div className="p-4 mt-6 bg-[#fcfcfd] border border-gray-200 rounded-3xl shadow-sm font-dmSans">
            <h2 className="text-2xl font-semibold text-[#7A1B29] mb-4">Días no laborables / Vacaciones</h2>
            <p className="text-gray-600 mb-6 text-sm">Agregá las fechas en las que la oficina permanecerá cerrada. Podés seleccionar un solo día o un rango completo.</p>

            <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 mb-8 items-end bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <div className="w-full md:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
                    <input type="date" name="fechaInicio" value={formData.fechaInicio} onChange={handleChange} required className="w-full px-4 py-2 border rounded-xl focus:ring-primary focus:border-primary" />
                </div>
                <div className="w-full md:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hasta (Opcional)</label>
                    <input type="date" name="fechaFin" value={formData.fechaFin} onChange={handleChange} min={formData.fechaInicio} className="w-full px-4 py-2 border rounded-xl focus:ring-primary focus:border-primary" />
                </div>
                <div className="w-full md:flex-grow">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (Ej: Vacaciones)</label>
                    <input type="text" name="motivo" value={formData.motivo} onChange={handleChange} required placeholder="¿Por qué está cerrado?" className="w-full px-4 py-2 border rounded-xl focus:ring-primary focus:border-primary" />
                </div>
                <button type="submit" disabled={isLoading} className="w-full md:w-auto px-6 py-2.5 bg-primary text-white font-semibold rounded-xl hover:bg-primaryHover transition-colors shrink-0">
                    {isLoading ? <Spinner /> : "Agregar"}
                </button>
            </form>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fechas</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Motivo</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {feriados.length === 0 && <tr><td colSpan="3" className="px-6 py-4 text-center text-gray-500">No hay feriados cargados</td></tr>}
                        {feriados.map((feriado) => (
                            <tr key={feriado.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {formatDisplayDate(feriado)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{feriado.motivo}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button onClick={() => handleDelete(feriado.id)} className="text-red-600 hover:text-red-900 bg-red-50 px-3 py-1 rounded-lg">Eliminar</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminFeriados;